import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { groupBy } from 'lodash'
import moment from 'moment';

interface WarehousesResortListState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };

    documents: any[];
    days: any[];
    schema: any;

    meta: any;
}

const state: WarehousesResortListState = {
    loading: false,
    loaded: false,
    fatalError: false,

    lastMeta: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
        totalPages: 0
    },

    documents: [],
    days: [],
    schema: null,

    meta: {},
};

export const ITEMS_LIST = 'GET_WAREHOUSE_RESORT_LIST';

export const actions: ActionTree<WarehousesResortListState, RootState> = {
    loadData({ commit, getters, dispatch }, query) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            const columns: any = {
                "id": "mv.id",
                "group_key": "mv.group_key",
                "warehouse_name": "w.name",
                "manager_name": "e.name",
                "product_name": "p.name",
                "product_container_name": "pc.name",
                "product_container_weight": "pc.weight",
                "from_product_name": "fp.name",
                "from_product_container_name": "fpc.name",
                "from_product_container_weight": "fpc.weight",
                "resort_date": "mv.resort_date",
                "quantity": "mv.quantity",
                "weight": "mv.weight",
                "created_at": "mv.created_at",
                "reasone": "mv.reasone",
            };

            const filter: any[] = [
              {$null: ['mv.deleted_at']}
            ];


            if (query.since) {
                filter.push({"$gte": ["mv.resort_date", {"$": moment(query.since).format('YYYY-MM-DD')}]})
            }
            if (query.until) {
                filter.push({"$lte": ["mv.resort_date", {"$": moment(query.until).format('YYYY-MM-DD')}] })
            }

            if (query.warehouse_id) {
                filter.push({ "$eq": ["mv.warehouse_id", {"$": query.warehouse_id}] })
            }

            vm.$db([
                vm.$q(ITEMS_LIST)
                .raw('documents.list')
                .action({
                    "columns": columns,
                    "from": "s:warehouses_resort",
                    "from_as": "mv",
                    "joins": [
                      {
                        "join": "s:products",
                        "type": "left",
                        "as":   "p",
                        "on": {
                          "$eq": ["mv.product_id", "p.id"]
                        }
                      },
                      {
                        "join": "s:product_containers",
                        "type": "left",
                        "as":   "pc",
                        "on": {
                          "$eq": ["mv.product_container_id", "pc.id"]
                        }
                      },
                      {
                        "join": "s:products",
                        "type": "left",
                        "as":   "fp",
                        "on": {
                          "$eq": ["mv.from_product_id", "fp.id"]
                        }
                      },
                      {
                        "join": "s:product_containers",
                        "type": "left",
                        "as":   "fpc",
                        "on": {
                          "$eq": ["mv.from_product_container_id", "fpc.id"]
                        }
                      },
                      {
                        "join": "s:employees",
                        "type": "left",
                        "as":   "e",
                        "on": {
                          "$eq": ["mv.manager_id", "e.id"]
                        }
                      },
                      {
                        "join": "s:warehouses",
                        "type": "left",
                        "as":   "w",
                        "on": {
                          "$eq": ["w.id", "mv.warehouse_id"]
                        }
                      }
                    ],
                    page_size: 100,
                    ...({page: parseInt(query.page) || 1}),
                    ...({
                        order: [
                            query.sortDirection == 'desc' ? { $asc: [columns[query.sortField || 'resort_date' ]] } : { $desc: [columns[query.sortField || 'resort_date']] }
                        ]
                    }),
                    ...(
                        filter.length > 1 ?
                        {
                            "where": {
                                "$and": filter
                            }
                        } : {
                            "where": filter[0]
                        }
                    )

                })
            ]).then(((result: ServerResult) => {
                commit('SET_ITEMS_LIST', result.getOperation(ITEMS_LIST).getContent());
                commit('LOADED_SUCCESS');

                resolve(null);
            }));
        })
    },

    exportData({state}, params) {
        return new Promise((resolve, reject) => {
            const csvContent: any[] = [];
            const header = [
                'Дата',
                'Склад',
                'Продукт источник',
                'Тара',
                'Продукт пополнения',
                'Тара',
                'Кол-во',
                'Менеджер',
                'Причина',
            ]
            csvContent.push(header);
            state.documents.forEach((item: any) => {
                csvContent.push([
                    moment(item.resort_date).format('DD.MM.YYYY'),
                    item.warehouse_name,
                    item.from_product_name,
                    item.from_product_container_name,
                    item.product_name,
                    item.product_container_name,
                    item.quantity,
                    item.manager_name,
                    item.reasone
                ]);
            })

            resolve(csvContent)
        })
    }

};

export const mutations: MutationTree<WarehousesResortListState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_ITEMS_LIST(state, data) {
        state.documents = Object.values(data.documents.reduce((acc: any, item: any) => {
          if (!acc[item.group_key]) {
            acc[item.group_key] = {
              resort_date: item.resort_date,
              warehouse_name: item.warehouse_name,
              quantity: 0,
              weight: 0,
              group_key: item.group_key,
              items: []
            }
          }

          acc[item.group_key].items.push(item)
          acc[item.group_key].quantity += item.quantity
          acc[item.group_key].weight += parseFloat(item.weight)

          return acc
        }, {}))

        state.schema = data.schema;
        state.meta = fetchEnumsFromSchema(data.schema);

        state.lastMeta = {
            page: data.meta.page,
            pageSize: data.meta.page_size,
            totalCount: data.meta.total_count,
            totalPages: data.meta.total_pages
        };
    },
};

const getters = {

};

export const STORE_KEY = '$_warehouses_resort_list';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
